import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button, Chip } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { formatCPF, formatName } from '@/lib/format';
import { useCliente, useDocumentos } from '@/modules/clientes/api';
import { decodeClienteId } from '@/modules/clientes/cliente-id';
import {
  buildChecks,
  buildSecoes,
  calcScore,
  classificacao,
  docsDoImovel,
} from '@/modules/clientes/conformidade';
import { ImovelDetailModal } from '@/modules/clientes/components/ImovelDetailModal';

export const Route = createFileRoute('/_authed/clientes_/$id_/relatorio/conformidade-resumido')({
  validateSearch: (search: Record<string, unknown>): { imprimir?: boolean } =>
    search.imprimir === true || search.imprimir === '1' ? { imprimir: true } : {},
  component: ConformidadeResumidoRoute,
});

const RELATORIO_NOME = 'Relatório de Conformidade Documental Resumido';

export function buildRelatorioConformidadeTitle(clienteNome: string) {
  return `${RELATORIO_NOME} - ${formatName(clienteNome)}`;
}

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #conf-resumido-print, #conf-resumido-print * { visibility: visible !important; }
  #conf-resumido-print {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    padding: 8px 12px !important;
  }
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  @page { size: A4 landscape; margin: 8mm; }
  #conf-resumido-print table {
    font-size: 7pt !important;
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
  }
  #conf-resumido-print th,
  #conf-resumido-print td {
    padding: 3px 5px !important;
    vertical-align: top !important;
    word-wrap: break-word !important;
    overflow-wrap: anywhere !important;
    white-space: normal !important;
    line-height: 1.25 !important;
  }
  #conf-resumido-print th { font-size: 6.5pt !important; }
  #conf-resumido-print .chip {
    font-size: 6pt !important;
    padding: 0 4px !important;
    white-space: normal !important;
  }
  #conf-resumido-print tr { page-break-inside: avoid !important; }
}
`;

function ConformidadeResumidoRoute() {
  const { id } = Route.useParams();
  const { imprimir } = Route.useSearch();
  const cpf = decodeClienteId(id);
  const navigate = useNavigate();
  const { data, isLoading, error } = useCliente(cpf);
  const { data: docs = [], isLoading: docsLoading } = useDocumentos(cpf);
  const [imovelDetail, setImovelDetail] = useState<Record<string, unknown> | null>(null);
  const autoPrintExecutado = useRef(false);

  useEffect(() => {
    if (!data?.nome) return;
    const previousTitle = document.title;
    document.title = buildRelatorioConformidadeTitle(data.nome);
    return () => {
      document.title = previousTitle;
    };
  }, [data?.nome]);

  useEffect(() => {
    if (!imprimir || isLoading || docsLoading || !data || autoPrintExecutado.current) return;
    autoPrintExecutado.current = true;
    const timeout = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(timeout);
  }, [data, docsLoading, imprimir, isLoading]);

  const back = (
    <Button
      variant="ghost"
      onClick={() => {
        if (window.history.length > 1) window.history.back();
        else void navigate({ to: '/clientes/$id', params: { id }, search: { relatorio: undefined, tab: 'relatorios', from_pipe: undefined, from_grupo: undefined } });
      }}
    >
      <ArrowLeft size={14} strokeWidth={1.5} />
      Voltar ao cliente
    </Button>
  );

  if (isLoading) {
    return (
      <PageShell eyebrow="Relatórios" title="Conformidade Documental Resumido" back={back}>
        <p className="text-sm text-text-muted">Carregando…</p>
      </PageShell>
    );
  }

  if (error || !data) {
    return (
      <PageShell eyebrow="Relatórios" title="Conformidade Documental Resumido" back={back}>
        <p className="text-sm text-danger">
          {error instanceof ApiError ? error.message : 'Falha ao carregar cliente.'}
        </p>
      </PageShell>
    );
  }

  const secoes = buildSecoes(data);
  const municipioUf = [data.municipio ? formatName(data.municipio) : null, data.uf]
    .filter(Boolean)
    .join(' / ');

  const linhas = secoes.map((s) => {
    const ds = docsDoImovel(docs, s);
    const checks = buildChecks(s.imovel, ds);
    if (s.vinculoPendente) {
      const onus = checks.find((c) => c.label === 'Ônus e Gravames');
      if (onus) { onus.status = 'pendente'; onus.detail = 'Análise depende do vínculo e da documentação do imóvel.'; }
      checks.push({label:'Vínculo do imóvel',status:'pendente',detail:'Contrato cadastrado sem vínculo de imóvel; conferir associação.'});
    }
    const score = calcScore(checks);
    const pendentes = checks.filter((c) => c.status !== 'ok');
    const cls = classificacao(score, pendentes.length);
    return { secao: s, score, pendentes, cls };
  });

  return (
    <PageShell eyebrow="Relatórios" title="Conformidade Documental Resumido" back={back}>
      <style>{PRINT_CSS}</style>
      <div id="conf-resumido-print">
        <Stack gap="md">
          <h1
            className="print-only text-text font-extrabold"
            style={{
              display: 'none',
              fontSize: '14pt',
              letterSpacing: '-0.02em',
              margin: '0 0 6px 0',
            }}
          >
            Relatório de Conformidade Documental Resumido
          </h1>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-2">
              <span
                className="uppercase font-semibold text-text-muted"
                style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
              >
                Cliente:
              </span>
              <span className="text-text font-semibold" style={{ fontSize: '0.95rem' }}>
                {formatName(data.nome)}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="uppercase font-semibold text-text-muted"
                style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
              >
                CPF:
              </span>
              <span className="text-text" style={{ fontSize: '0.9rem' }}>
                {formatCPF(data.cpf)}
              </span>
              {municipioUf && (
                <Chip tone="info" size="sm">
                  {municipioUf}
                </Chip>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap no-print">
            <button
              type="button"
              onClick={() => window.print()}
              className="chip chip-danger cursor-pointer inline-flex items-center gap-1"
              style={{ fontSize: '0.78rem' }}
              aria-label="Gerar PDF"
            >
              <FileText size={11} strokeWidth={1.8} />
              PDF
            </button>
          </div>

          {linhas.length === 0 ? (
            <div className="empty-state py-10">
              <p className="empty-state-title">Sem imóveis</p>
              <p className="empty-state-desc">Adicione um imóvel ou arrendamento.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm w-full">
                <thead>
                  <tr>
                    <th style={{ width: '9%' }}>Tipo</th>
                    <th style={{ width: '11%' }}>Matrícula</th>
                    <th style={{ width: '18%' }}>Imóvel</th>
                    <th style={{ width: '9%' }}>Hectares</th>
                    <th style={{ width: '7%' }}>Score</th>
                    <th style={{ width: '6%' }}>Pend.</th>
                    <th>Principal Pendência</th>
                    <th style={{ width: '13%' }}>Classificação</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map(({ secao, score, pendentes, cls }) => {
                    const scoreTone: 'ok' | 'warn' | 'danger' =
                      score >= 80 ? 'ok' : score >= 50 ? 'warn' : 'danger';
                    return (
                      <tr
                        key={secao.key}
                        onClick={() => { if (secao.imovel.id != null) setImovelDetail(secao.imovel); }}
                        className="cursor-pointer hover:bg-surface2"
                        title="Ver detalhes do imóvel"
                      >
                        <td>
                          <Chip tone={secao.tipo === 'Próprio' ? 'ok' : 'accent'} size="sm">
                            {secao.tipo}
                          </Chip>
                        </td>
                        <td>
                          {secao.matricula ? (
                            <Chip tone="info" size="sm">
                              {secao.matricula}
                            </Chip>
                          ) : (
                            <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                              —
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>
                          <span className="text-text font-medium">{secao.denominacao}</span>
                        </td>
                        <td>
                          {secao.areaConjunta != null ? <span>Não individualizada<small className="block">Contrato: {secao.areaConjunta.toLocaleString('pt-BR',{minimumFractionDigits:4})} ha conjuntos</small></span> : secao.area != null ? (
                            <Chip tone="accent" size="sm">
                              {secao.area.toLocaleString('pt-BR')} ha
                            </Chip>
                          ) : (
                            <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                              —
                            </span>
                          )}
                        </td>
                        <td>
                          <Chip tone={scoreTone} size="sm">
                            {score}%
                          </Chip>
                        </td>
                        <td>
                          <Chip
                            tone={pendentes.length === 0 ? 'ok' : pendentes.length > 2 ? 'danger' : 'warn'}
                            size="sm"
                          >
                            {pendentes.length}
                          </Chip>
                        </td>
                        <td style={{ fontSize: '0.82rem' }} className="text-text-2">
                          {pendentes.length === 0
                            ? '—'
                            : pendentes.map((p) => p.label).join(', ')}
                        </td>
                        <td>
                          <Chip tone={cls.tone} size="sm">
                            {cls.label}
                          </Chip>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Stack>
      </div>
      <ImovelDetailModal
        imovel={imovelDetail}
        cpfDono={data.cpf}
        nomeDono={data.nome}
        onClose={() => setImovelDetail(null)}
      />
    </PageShell>
  );
}
